import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Button,
  IconButton,
  Paper,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Help as HelpIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

import { GenerativeBatchEditorProps } from '../types';

export const GenerativeBatchEditor: React.FC<GenerativeBatchEditorProps> = ({
  batch,
  onChange,
  onAddContextMapping,
  onRemoveContextMapping,
  onAddOutputMapping,
  onRemoveOutputMapping,
  newContextMapSource,
  setNewContextMapSource,
  newContextMapTarget,
  setNewContextMapTarget,
  newOutputMapSource,
  setNewOutputMapSource,
  newOutputMapTarget,
  setNewOutputMapTarget,
}) => (
  <Box>
    <Alert severity="info" sx={{ mb: 2 }}>
      <Typography variant="body2">
        <strong>Generative Batch:</strong> Use AI to generate content based on context
      </Typography>
    </Alert>

    {/* Basic Configuration */}
    <Box display="flex" gap={2} mb={2}>
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel id="target-select-label">Target</InputLabel>
        <Select
          labelId="target-select-label"
          label="Target"
          value={batch.target || 'form'}
          onChange={(e) => onChange({ ...batch, target: e.target.value })}
        >
          <MenuItem value="form">Form Fields</MenuItem>
          <MenuItem value="context">Context Store</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        size="small"
        value={batch.generationId || ''}
        onChange={(e) => onChange({ ...batch, generationId: e.target.value })}
        label="Generation ID"
        placeholder="Enter generation ID"
      />
    </Box>

    {/* Context to Prompt Mapping */}
    <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
      Context to Prompt Mapping
      <Tooltip title="Map context values to AI prompt parameters">
        <HelpIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
      </Tooltip>
    </Typography>

    {/* Existing context mappings */}
    {(batch.contextToPromptMap || []).map((mapping: any, index: number) => (
      <Paper
        key={mapping.id || `context-map-${index}`}
        sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}
      >
        <TextField
          size="small"
          value={mapping.source || ''}
          onChange={(e) => {
            const newMappings = [...(batch.contextToPromptMap || [])];
            newMappings[index] = { ...mapping, source: e.target.value };
            onChange({ ...batch, contextToPromptMap: newMappings });
          }}
          label="Context Source"
          placeholder="cause, location"
          sx={{ flex: 1 }}
        />
        <Box display="flex" alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
          <ArrowForwardIcon fontSize="small" />
          <Typography variant="caption">is sent to</Typography>
        </Box>
        <TextField
          size="small"
          value={mapping.target || ''}
          onChange={(e) => {
            const newMappings = [...(batch.contextToPromptMap || [])];
            newMappings[index] = { ...mapping, target: e.target.value };
            onChange({ ...batch, contextToPromptMap: newMappings });
          }}
          label="Prompt Parameter"
          placeholder="cause, tone, location"
          sx={{ flex: 1 }}
        />
        <IconButton onClick={() => onRemoveContextMapping(index)} color="error">
          <DeleteIcon />
        </IconButton>
      </Paper>
    ))}

    {/* Add new context mapping */}
    <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      <TextField
        size="small"
        value={newContextMapSource}
        onChange={(e) => setNewContextMapSource(e.target.value)}
        label="Context Source"
        placeholder="cause, campaignVibe"
        sx={{ flex: 1 }}
      />
      <Box display="flex" alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
        <ArrowForwardIcon fontSize="small" />
        <Typography variant="caption">is sent to</Typography>
      </Box>
      <TextField
        size="small"
        value={newContextMapTarget}
        onChange={(e) => setNewContextMapTarget(e.target.value)}
        label="Prompt Parameter"
        placeholder="cause, tone"
        sx={{ flex: 1 }}
      />
      <Button onClick={onAddContextMapping} startIcon={<AddIcon />}>
        Add
      </Button>
    </Paper>

    {/* Output to Field Mapping */}
    <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
      Output to Field Mapping
      <Tooltip title="Map AI generation outputs to form fields or context">
        <HelpIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
      </Tooltip>
    </Typography>

    {/* Existing output mappings */}
    {(batch.outputToFieldMap || []).map((mapping: any, index: number) => (
      <Paper
        key={mapping.id || `output-map-${index}`}
        sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}
      >
        <TextField
          size="small"
          value={mapping.source || ''}
          onChange={(e) => {
            const newMappings = [...(batch.outputToFieldMap || [])];
            newMappings[index] = { ...mapping, source: e.target.value };
            onChange({ ...batch, outputToFieldMap: newMappings });
          }}
          label="Generation Output"
          placeholder="title, description, cta"
        />
        <TextField
          size="small"
          fullWidth
          value={mapping.target || ''}
          onChange={(e) => {
            const newMappings = [...(batch.outputToFieldMap || [])];
            newMappings[index] = { ...mapping, target: e.target.value };
            onChange({ ...batch, outputToFieldMap: newMappings });
          }}
          label={batch.target === 'context' ? 'Context Path' : 'Form Field'}
          placeholder={batch.target === 'context' ? 'generatedContent.title' : 'title'}
        />
        <IconButton onClick={() => onRemoveOutputMapping(index)} color="error">
          <DeleteIcon />
        </IconButton>
      </Paper>
    ))}

    {/* Add new output mapping */}
    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      <TextField
        size="small"
        value={newOutputMapSource}
        onChange={(e) => setNewOutputMapSource(e.target.value)}
        label="Generation Output"
        placeholder="title, description"
      />
      <TextField
        size="small"
        fullWidth
        value={newOutputMapTarget}
        onChange={(e) => setNewOutputMapTarget(e.target.value)}
        label={batch.target === 'context' ? 'Context Path' : 'Form Field'}
        placeholder={batch.target === 'context' ? 'generated.title' : 'title'}
      />
      <Button onClick={onAddOutputMapping} startIcon={<AddIcon />}>
        Add
      </Button>
    </Paper>

    <Alert severity="info" sx={{ mt: 2 }}>
      <Typography variant="body2">
        <strong>Context Mapping:</strong> Map context values to AI prompt parameters
        <br />
        <strong>Output Mapping:</strong> Map AI outputs to{' '}
        {batch.target === 'context' ? 'context paths' : 'form fields'}
        <br />
        <strong>Processing:</strong> Streaming support, error handling with retry capability
      </Typography>
    </Alert>
  </Box>
);
